import { useRef } from "react";
import type { GroupImperativeHandle } from "react-resizable-panels";

interface PanelLayout {
	[id: string]: number;
	table: number;
	details: number;
}

interface UseResizablePanelsOptions {
	resetLayout?: PanelLayout;
}

const DEFAULT_PANEL_LAYOUT: PanelLayout = { table: 50, details: 50 };

export const useResizablePanels = (
	storageKey: string,
	options: UseResizablePanelsOptions = {},
) => {
	const groupRef = useRef<GroupImperativeHandle>(null);

	const getSavedPanelLayout = (): PanelLayout => {
		try {
			const saved = localStorage.getItem(storageKey);
			if (saved) {
				const layout = JSON.parse(saved);
				if (layout && typeof layout === "object") {
					return layout as PanelLayout;
				}
			}
		} catch (e) {
			console.error("Error loading panel layout:", e);
		}
		return DEFAULT_PANEL_LAYOUT;
	};

	const handlePanelResize = (layout: { [id: string]: number }) => {
		try {
			localStorage.setItem(storageKey, JSON.stringify(layout));
		} catch (e) {
			console.error("Error saving panel layout:", e);
		}
	};

	const resetPanelLayout = () => {
		if (groupRef.current) {
			const defaultLayout = options.resetLayout ?? DEFAULT_PANEL_LAYOUT;
			groupRef.current.setLayout(defaultLayout);
			try {
				localStorage.setItem(storageKey, JSON.stringify(defaultLayout));
			} catch (e) {
				console.error("Error saving default layout:", e);
			}
		}
	};

	const handleSeparatorDoubleClick = () => {
		resetPanelLayout();
	};

	return {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	};
};
